import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Calendar, CreditCard, ChevronDown, Search, Filter, ArrowUpDown, X, Edit, Eye } from "lucide-react";
import { Sidebar } from "@/components/ui/sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { AdminPlan, AdminSubscription } from "@/types/admin";

export default function CustomerSubscriptionsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("all");
  const [sortBy, setSortBy] = useState("startDate");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedSubscription, setSelectedSubscription] = useState<any>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showChangePlanDialog, setShowChangePlanDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [newPlanId, setNewPlanId] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch subscriptions
  const { data: subscriptions = [], isLoading: isLoadingSubscriptions } = useQuery<AdminSubscription[]>({
    queryKey: ["/api/admin/subscriptions"],
  });

  // Fetch subscription plans for filtering
  const { data: plans = [] } = useQuery<AdminPlan[]>({
    queryKey: ["/api/subscription-plans/all"],
  });

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Invoice generation mutation
  const generateInvoiceMutation = useMutation({
    mutationFn: async ({ subscriptionId, planId }: { subscriptionId: number; planId: number }) => {
      return apiRequest('POST', `/api/admin/subscriptions/${subscriptionId}/generate-upgrade-invoice`, { planId });
    },
    onSuccess: (data: any) => {
      console.log('Invoice generation response:', data);
      setSelectedInvoice(data);
      setShowInvoiceDialog(true);
      setShowChangePlanDialog(false);
      setSelectedSubscription(null);
      toast({
        title: "Invoice Generated",
        description: `Upgrade invoice created: $${data.details?.proRatedAmount} for remaining days`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to generate upgrade invoice",
        variant: "destructive",
      });
    }
  });

  // Payment processing mutation  
  const markPaidMutation = useMutation({
    mutationFn: async (invoiceId: number) => {
      return apiRequest('PATCH', `/api/admin/invoices/${invoiceId}/mark-paid`, {});
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/subscriptions'] });
      setShowInvoiceDialog(false);
      setSelectedInvoice(null);
      toast({
        title: "Payment Processed",
        description: data.message || "Subscription upgraded successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error", 
        description: error.message || "Failed to process payment",
        variant: "destructive",
      });
    }
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: async (subscriptionId: number) => {
      return apiRequest('PATCH', `/api/admin/subscriptions/${subscriptionId}/cancel`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/subscriptions'] });
      toast({
        title: "Success",
        description: "Subscription cancelled successfully",
      });
      setShowCancelDialog(false);
      setSelectedSubscription(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel subscription",
        variant: "destructive",
      });
    }
  });

  const reactivateSubscriptionMutation = useMutation({
    mutationFn: async (subscriptionId: number) => {
      return apiRequest('PATCH', `/api/admin/subscriptions/${subscriptionId}/reactivate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/subscriptions'] });
      toast({
        title: "Success",
        description: "Subscription reactivated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to reactivate subscription",
        variant: "destructive",
      });
    }
  });

  const handleViewDetails = (subscription: any) => {
    setSelectedSubscription(subscription);
    setShowDetailsDialog(true);
  };

  const handleChangePlan = (subscription: any) => {
    setSelectedSubscription(subscription);
    setNewPlanId(subscription.planId.toString());
    setShowChangePlanDialog(true);
  };

  const handleCancelSubscription = (subscription: any) => {
    setSelectedSubscription(subscription);
    setShowCancelDialog(true);
  };

  const handleReactivate = (subscription: any) => {
    reactivateSubscriptionMutation.mutate(subscription.id);
  };

  // Filter and sort subscriptions
  const filterSubscriptions = (status: string) => {
    let filtered = subscriptions.filter((sub: any) => {
      const matchesSearch = 
        sub.business?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sub.plan?.name.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesPlan = selectedPlan === "all" || sub.planId.toString() === selectedPlan;
      const matchesStatus = status === "all" || sub.status === status;
      
      return matchesSearch && matchesPlan && matchesStatus;
    });

    // Sort subscriptions
    filtered.sort((a: any, b: any) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case "business":
          aValue = a.business?.name || "";
          bValue = b.business?.name || "";
          break;
        case "plan":
          aValue = a.plan?.name || "";
          bValue = b.plan?.name || "";
          break;
        case "price":
          aValue = parseFloat(a.plan?.price || 0);
          bValue = parseFloat(b.plan?.price || 0);
          break;
        case "startDate":
        default:
          aValue = new Date(a.startDate);
          bValue = new Date(b.startDate);
          break;
      }
      
      if (sortOrder === "asc") {
        return aValue < bValue ? -1 : 1;
      } else {
        return aValue > bValue ? -1 : 1;
      }
    });

    return filtered;
  };

  const activeSubscriptions = filterSubscriptions("active");
  const expiredSubscriptions = filterSubscriptions("expired");
  const cancelledSubscriptions = filterSubscriptions("canceled");

  const SubscriptionTable = ({ subscriptions, status }: { subscriptions: any[], status: string }) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <Button variant="ghost" size="sm" onClick={() => {
              setSortBy("business");
              setSortOrder(sortBy === "business" && sortOrder === "asc" ? "desc" : "asc");
            }}>
              Business <ArrowUpDown className="h-4 w-4 ml-1" />
            </Button>
          </TableHead>
          <TableHead>
            <Button variant="ghost" size="sm" onClick={() => {
              setSortBy("plan");
              setSortOrder(sortBy === "plan" && sortOrder === "asc" ? "desc" : "asc");
            }}>
              Plan <ArrowUpDown className="h-4 w-4 ml-1" />
            </Button>
          </TableHead>
          <TableHead>Status</TableHead>
          <TableHead>
            <Button variant="ghost" size="sm" onClick={() => {
              setSortBy("startDate");
              setSortOrder(sortBy === "startDate" && sortOrder === "asc" ? "desc" : "asc");
            }}>
              Start Date <ArrowUpDown className="h-4 w-4 ml-1" />
            </Button>
          </TableHead>
          <TableHead>
            <Button variant="ghost" size="sm" onClick={() => {
              setSortBy("price");
              setSortOrder(sortBy === "price" && sortOrder === "asc" ? "desc" : "asc");
            }}>
              Revenue <ArrowUpDown className="h-4 w-4 ml-1" />
            </Button>
          </TableHead>
          {status === "expired" && <TableHead>End Date</TableHead>}
          {status === "canceled" && <TableHead>Cancelled Date</TableHead>}
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoadingSubscriptions ? (
          Array.from({ length: 5 }).map((_, index) => (
            <TableRow key={index}>
              <TableCell><Skeleton className="h-5 w-32" /></TableCell>
              <TableCell><Skeleton className="h-5 w-24" /></TableCell>
              <TableCell><Skeleton className="h-5 w-16" /></TableCell>
              <TableCell><Skeleton className="h-5 w-20" /></TableCell>
              <TableCell><Skeleton className="h-5 w-20" /></TableCell>
              {(status === "expired" || status === "canceled") && <TableCell><Skeleton className="h-5 w-20" /></TableCell>}
              <TableCell><Skeleton className="h-8 w-20" /></TableCell>
            </TableRow>
          ))
        ) : subscriptions.length === 0 ? (
          <TableRow>
            <TableCell colSpan={status === "active" ? 6 : 7} className="text-center py-8 text-muted-foreground">
              No {status} subscriptions found
              {searchQuery && <div className="text-sm mt-1">Try adjusting your search or filters</div>}
            </TableCell>
          </TableRow>
        ) : (
          subscriptions.map((subscription: any) => (
            <TableRow key={subscription.id}>
              <TableCell>
                <div className="font-medium">{subscription.business?.name || 'Unknown Business'}</div>
                <div className="text-sm text-muted-foreground">ID: {subscription.business?.id}</div>
              </TableCell>
              <TableCell>
                <div className="font-medium">{subscription.plan?.name}</div>
                <div className="text-sm text-muted-foreground">${subscription.plan?.price}/month</div>
              </TableCell>
              <TableCell>
                <Badge 
                  variant={
                    subscription.status === "active" ? "default" : 
                    subscription.status === "canceled" ? "destructive" : 
                    subscription.status === "expired" ? "secondary" : "outline"
                  }
                >
                  {subscription.status}
                </Badge>
              </TableCell>
              <TableCell>{formatDate(subscription.startDate)}</TableCell>
              <TableCell>
                <div className="font-medium">{formatCurrency(subscription.plan?.price || 0)}</div>
              </TableCell>
              {status === "expired" && (
                <TableCell>{subscription.endDate ? formatDate(subscription.endDate) : 'N/A'}</TableCell>
              )}
              {status === "canceled" && (
                <TableCell>{subscription.endDate ? formatDate(subscription.endDate) : 'N/A'}</TableCell>
              )}
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      Actions
                      <ChevronDown className="h-4 w-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem 
                      onClick={() => handleViewDetails(subscription)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </DropdownMenuItem>
                    {status === "active" && (
                      <>
                        <DropdownMenuItem
                          onClick={() => handleChangePlan(subscription)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Change Plan
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="text-destructive"
                          onClick={() => handleCancelSubscription(subscription)}
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancel Subscription
                        </DropdownMenuItem>
                      </>
                    )}
                    {(status === "expired" || status === "canceled") && (
                      <DropdownMenuItem
                        onClick={() => handleReactivate(subscription)}
                      >
                        <CreditCard className="h-4 w-4 mr-2" />
                        Reactivate
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 ml-64">
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Customer Subscriptions</h1>
          <p className="text-muted-foreground">
            Monitor and manage all customer subscriptions across your platform
          </p>
        </div>

        {/* Filters and Search */}
        <Card className="mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Filters & Search</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by business name or plan..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Filter by plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  {plans.map((plan: any) => (
                    <SelectItem key={plan.id} value={plan.id.toString()}>
                      {plan.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Tabs */}
        <Tabs defaultValue="active" className="space-y-4">
          <TabsList>
            <TabsTrigger value="active">
              Active ({activeSubscriptions.length})
            </TabsTrigger>
            <TabsTrigger value="expired">
              Expired ({expiredSubscriptions.length})
            </TabsTrigger>
            <TabsTrigger value="canceled">
              Cancelled ({cancelledSubscriptions.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            <Card>
              <CardHeader>
                <CardTitle>Active Subscriptions</CardTitle>
                <CardDescription>
                  Currently active customer subscriptions generating revenue
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <SubscriptionTable subscriptions={activeSubscriptions} status="active" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="expired">
            <Card>
              <CardHeader>
                <CardTitle>Expired Subscriptions</CardTitle>
                <CardDescription>
                  Subscriptions that have reached their end date
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <SubscriptionTable subscriptions={expiredSubscriptions} status="expired" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="canceled">
            <Card>
              <CardHeader>
                <CardTitle>Cancelled Subscriptions</CardTitle>
                <CardDescription>
                  Subscriptions that were cancelled by customers or admins
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <SubscriptionTable subscriptions={cancelledSubscriptions} status="canceled" />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{activeSubscriptions.length}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(
                  activeSubscriptions.reduce((total: number, sub: any) => total + parseFloat(sub.plan?.price || 0), 0)
                )}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Expired</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{expiredSubscriptions.length}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Cancelled</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{cancelledSubscriptions.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Subscription Details Dialog */}
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Subscription Details</DialogTitle>
              <DialogDescription>
                Complete information about this subscription
              </DialogDescription>
            </DialogHeader>
            {selectedSubscription && (
              <div className="grid grid-cols-2 gap-4 py-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Business</Label>
                  <p className="text-sm text-muted-foreground">{selectedSubscription.business?.name}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Client ID</Label>
                  <p className="text-sm text-muted-foreground">#{String(selectedSubscription.business?.id).padStart(12, '0')}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Plan</Label>
                  <p className="text-sm text-muted-foreground">{selectedSubscription.plan?.name}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Price</Label>
                  <p className="text-sm text-muted-foreground">{formatCurrency(parseFloat(selectedSubscription.plan?.price || 0))}/month</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Status</Label>
                  <Badge variant={selectedSubscription.status === 'active' ? 'default' : 'secondary'}>
                    {selectedSubscription.status}
                  </Badge>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Start Date</Label>
                  <p className="text-sm text-muted-foreground">{formatDate(selectedSubscription.startDate)}</p>
                </div>
                {selectedSubscription.endDate && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">End Date</Label>
                    <p className="text-sm text-muted-foreground">{formatDate(selectedSubscription.endDate)}</p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Stripe Customer ID</Label>
                  <p className="text-sm text-muted-foreground">{selectedSubscription.stripeCustomerId || 'N/A'}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Stripe Subscription ID</Label>
                  <p className="text-sm text-muted-foreground">{selectedSubscription.stripeSubscriptionId || 'N/A'}</p>
                </div>
                {selectedSubscription.plan?.description && (
                  <div className="col-span-2 space-y-2">
                    <Label className="text-sm font-medium">Plan Description</Label>
                    <p className="text-sm text-muted-foreground">{selectedSubscription.plan.description}</p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change Plan Dialog */}
        <Dialog open={showChangePlanDialog} onOpenChange={setShowChangePlanDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Subscription Plan</DialogTitle>
              <DialogDescription>
                Select a new plan for {selectedSubscription?.business?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="current-plan">Current Plan</Label>
                <p className="text-sm text-muted-foreground">{selectedSubscription?.plan?.name}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-plan">New Plan</Label>
                <Select value={newPlanId} onValueChange={setNewPlanId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select new plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.filter((plan: any) => plan.id !== selectedSubscription?.planId).map((plan: any) => (
                      <SelectItem key={plan.id} value={plan.id.toString()}>
                        {plan.name} - {formatCurrency(parseFloat(plan.price || 0))}/month
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowChangePlanDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => generateInvoiceMutation.mutate({ 
                  subscriptionId: selectedSubscription?.id, 
                  planId: parseInt(newPlanId) 
                })}
                disabled={!newPlanId || generateInvoiceMutation.isPending}
              >
                {generateInvoiceMutation.isPending ? "Generating Invoice..." : "Generate Invoice"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel Subscription Alert Dialog */}
        <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to cancel the subscription for {selectedSubscription?.business?.name}? 
                This action cannot be undone and the customer will lose access to their current plan.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => cancelSubscriptionMutation.mutate(selectedSubscription?.id)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={cancelSubscriptionMutation.isPending}
              >
                {cancelSubscriptionMutation.isPending ? "Cancelling..." : "Cancel Subscription"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      </main>

      {/* Invoice Payment Dialog */}
      {selectedInvoice && (
        <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Subscription Upgrade Invoice</DialogTitle>
              <DialogDescription>
                Review and process payment for subscription upgrade
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="border rounded p-3 bg-blue-50 dark:bg-blue-950">
                <h4 className="font-bold text-sm mb-2">INVOICE #{selectedInvoice.invoice?.id}</h4>
                <div className="text-xs text-muted-foreground">
                  Generated: {new Date().toLocaleDateString()} | Due: {selectedInvoice.invoice?.due_date ? new Date(selectedInvoice.invoice.due_date).toLocaleDateString() : 'N/A'}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Business:</span>
                  <p>{selectedInvoice.details?.businessName}</p>
                </div>
                <div>
                  <span className="font-medium">Current Plan:</span>
                  <p>{selectedInvoice.details?.currentPlan}</p>
                </div>
                <div>
                  <span className="font-medium">Upgrade To:</span>
                  <p>{selectedInvoice.details?.targetPlan}</p>
                </div>
                <div>
                  <span className="font-medium">Pro-rated Amount:</span>
                  <p className="text-lg font-bold text-green-600">${selectedInvoice.details?.proRatedAmount}</p>
                </div>
              </div>
              
              <div className="bg-muted p-3 rounded">
                <p className="text-sm">
                  <strong>Description:</strong> {selectedInvoice.invoice?.description}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Pro-rated for {selectedInvoice.details?.remainingDays} remaining days in current billing cycle
                </p>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1">
                  📄 View Full Invoice
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => {
                    // Email invoice to customer
                    toast({
                      title: "Invoice Sent",
                      description: "Payment link sent to customer email",
                    });
                  }}
                >
                  📧 Send Payment Link
                </Button>
              </div>
            </div>

            <DialogFooter className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowInvoiceDialog(false)}
              >
                Cancel
              </Button>
              {selectedInvoice.paymentIntent ? (
                <Button
                  onClick={() => {
                    // This would open customer payment portal
                    window.open(`/payment/${selectedInvoice.paymentIntent.id}`, '_blank');
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  🔗 Open Customer Payment Portal
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    console.log('Selected invoice:', selectedInvoice);
                    markPaidMutation.mutate(selectedInvoice.invoice?.id);
                  }}
                  disabled={markPaidMutation.isPending}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {markPaidMutation.isPending ? "Processing..." : "Mark as Paid (Admin Override)"}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
